import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerAPI } from "../../utils/api";
import { useParams, Link } from "react-router-dom";
import LiveTrackMap from "../../components/LiveTrackMap";
import {
  MapPin,
  Navigation,
  Home,
  Package,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  ChevronLeft,
  Phone,
  User,
  Weight,
  DollarSign,
  Calendar,
  RefreshCw,
} from "lucide-react";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const statusSteps = [
  {
    id: "pending",
    label: "Order Placed",
    icon: Home,
    description: "Your order has been received",
    color: "amber",
  },
  {
    id: "assigned",
    label: "Courier Assigned",
    icon: Truck,
    description: "A courier has been assigned",
    color: "blue",
  },
  {
    id: "picked_up",
    label: "Picked Up",
    icon: Package,
    description: "Package collected from sender",
    color: "violet",
  },
  {
    id: "in_transit",
    label: "In Transit",
    icon: Navigation,
    description: "Package is on the way",
    color: "cyan",
  },
  {
    id: "delivered",
    label: "Delivered",
    icon: CheckCircle,
    description: "Package delivered successfully",
    color: "emerald",
  },
];

const statusConfig = {
  pending: { color: "bg-amber-100 text-amber-800", icon: Clock },
  assigned: { color: "bg-blue-100 text-blue-800", icon: Truck },
  picked_up: { color: "bg-violet-100 text-violet-800", icon: Package },
  in_transit: { color: "bg-cyan-100 text-cyan-800", icon: Navigation },
  delivered: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  failed: { color: "bg-rose-100 text-rose-800", icon: AlertCircle },
  cancelled: { color: "bg-gray-100 text-gray-800", icon: AlertCircle },
};

const CustomerLiveTrack = () => {
  const { id } = useParams();
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 seconds

  const {
    data: trackData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["liveTrack", id],
    queryFn: () => customerAPI.liveTrack(id),
    refetchInterval: refreshInterval,
    retry: 2,
    enabled: !!id,
  });

  const data = trackData?.data?.data;
  const order = data?.order;
  const courier = data?.courier;
  const courierLocation = data?.courier_location;
  const routePoints = data?.route_points || [];
  const trackingHistory = data?.tracking_history || [];
  const statusProgress = data?.status_progress;
  const eta = data?.eta;
  const isLive = data?.is_live || false;

  const currentStatus = order?.status || "pending";
  const currentStepIndex = statusSteps.findIndex((s) => s.id === currentStatus);

  // Auto-refresh toggle
  const toggleRefresh = () => {
    setRefreshInterval((prev) => (prev ? 0 : 10000));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="font-medium text-lg">Failed to load tracking</p>
        <p className="text-sm mt-1">{error.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 btn-secondary flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        <Package className="w-12 h-12 text-gray-300 mb-4" />
        <p className="font-medium text-lg">Order not found</p>
        <Link to="/customer/orders" className="mt-4 btn-primary">
          Back to Orders
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/customer/orders"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Live Tracking</h1>
            <p className="text-gray-500 mt-1">
              Order{" "}
              <span className="font-mono font-semibold">
                {order.tracking_number}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleRefresh}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              refreshInterval
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-gray-50 text-gray-600 border border-gray-200"
            }`}
          >
            {refreshInterval ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span>Live Updates</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Paused</span>
              </>
            )}
          </button>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh now"
          >
            <RefreshCw
              className={`w-5 h-5 text-gray-600 ${isRefetching ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div
        className={`card bg-gradient-to-r ${
          currentStatus === "delivered"
            ? "from-emerald-50 to-emerald-100/50 border-emerald-200"
            : currentStatus === "in_transit"
              ? "from-cyan-50 to-cyan-100/50 border-cyan-200"
              : "from-primary-50 to-primary-100/50 border-primary-200"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={`p-3 rounded-xl ${
                statusConfig[currentStatus]?.color || "bg-gray-100"
              }`}
            >
              {(() => {
                const Icon = statusConfig[currentStatus]?.icon || Package;
                return <Icon className="w-8 h-8" />;
              })()}
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Status</p>
              <p className="text-2xl font-bold text-gray-900 capitalize">
                {currentStatus.replace("_", " ")}
              </p>
              {eta && currentStatus === "in_transit" && (
                <p className="text-sm text-cyan-600 font-medium mt-1">
                  Estimated arrival: {eta.formatted}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Estimated Delivery</p>
            <p className="text-lg font-semibold text-gray-900">
              {order.estimated_delivery_at
                ? formatDate(order.estimated_delivery_at)
                : "Calculating..."}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Delivery Progress
        </h3>
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-700"
              style={{
                width: `${statusProgress?.percent || 0}%`,
              }}
            />
          </div>

          <div className="relative grid grid-cols-5 gap-2">
            {statusSteps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;

              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center text-center"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all z-10 ${
                      isCompleted
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-400"
                    } ${isCurrent ? "ring-4 ring-primary-100" : ""}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <p
                    className={`text-xs font-semibold mt-2 ${
                      isCompleted ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Live Map */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-blue-500" />
            Live Location
          </h3>
          {isLive && (
            <div className="flex items-center space-x-2 text-sm text-emerald-600 font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span>Tracking Active</span>
            </div>
          )}
        </div>
        <LiveTrackMap
          routePoints={routePoints}
          courierLocation={courierLocation}
          isLive={isLive}
          status={currentStatus}
        />
      </div>

      {/* Courier Info */}
      {courier && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">
                  Your Courier
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {courier.name}
                </p>
                <p className="text-sm text-gray-600">
                  {courier.vehicle_type || "Motorcycle"}
                </p>
              </div>
            </div>
            {courier.phone && (
              <a
                href={`tel:${courier.phone}`}
                className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">Call</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Order Details & Tracking History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Details */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="w-5 h-5 mr-2 text-amber-500" />
            Order Details
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
              <Home className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">
                  Pickup
                </p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {order.pickup_address}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
              <MapPin className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">
                  Delivery
                </p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {order.delivery_address}
                </p>
                <div className="flex items-center mt-2 text-sm text-gray-600">
                  <User className="w-3.5 h-3.5 mr-1" />
                  {order.recipient_name}
                  <Phone className="w-3.5 h-3.5 ml-3 mr-1" />
                  {order.recipient_phone}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Package Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {order.package_type}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Weight</p>
                <p className="font-medium text-gray-900">{order.weight} kg</p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Amount</span>
                <span className="font-bold text-primary-600 text-lg">
                  EGP {formatMoney(order.total_amount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tracking History */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-blue-500" />
            Tracking History
          </h3>
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
            {trackingHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No tracking updates yet</p>
              </div>
            ) : (
              trackingHistory.map((event, index) => {
                const isLatest = index === 0;
                const EventIcon = statusConfig[event.status]?.icon || Clock;
                return (
                  <div
                    key={index}
                    className={`flex items-start space-x-3 p-3 rounded-xl transition-colors ${
                      isLatest
                        ? "bg-primary-50 border border-primary-100"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg flex-shrink-0 ${
                        isLatest
                          ? "bg-primary-100 text-primary-600"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      <EventIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p
                          className={`text-sm font-medium ${
                            isLatest ? "text-primary-900" : "text-gray-900"
                          }`}
                        >
                          {event.status?.replace("_", " ")?.toUpperCase()}
                        </p>
                        {isLatest && (
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                            Latest
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {event.location || "Location unknown"}
                      </p>
                      {event.notes && (
                        <p className="text-xs text-gray-500 mt-1">
                          {event.notes}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(event.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLiveTrack;
